import React, { useState } from 'react';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { IoChatbubbleEllipsesOutline } from 'react-icons/io5';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import './AgentDashboard.css';
import AssignedProperties from './components/AssignedProperties';
import AgentAnalytics from './components/Analytics';
import Notifications from '../shared/Notifications/Notifications';
import ManageTenants from './components/ManageTenants';
import ManageRentals from './components/ManageRentals';
import RentStatus from '../Landlord/components/RentStatus';
import RentStatusDetails from '../Landlord/components/RentStatusDetails';
import Profile from '../Profile/Profile';
import Messages from '../shared/Messages/Messages';

import AgentUnitRequests from './components/AgentUnitRequests';
import RentEvidenceView from '../Landlord/components/RentEvidenceView';
import Landlords from './components/Landlords';
import WaterUsageView from '../Landlord/components/WaterUsageView';
import PropertyMaintenanceView from '../Landlord/components/PropertyMaintenanceView';
import MoveOutRequests from './components/MoveOutRequests';
import TenantScore from '../Landlord/components/TenantScore';
import PropertyRatings from '../shared/PropertyRatings/PropertyRatings';


const AgentDashboard = () => {
  const [activeSection, setActiveSection] = useState('properties');
  const [navigationState, setNavigationState] = useState({});

  useScrollToTop(activeSection);

  const handleNavigate = (section, state = {}) => {
    setActiveSection(section);
    setNavigationState(state);
  };

  const sidebarItems = [
    { id: 'properties', label: 'Assigned Properties', icon: '🏡', section: 'MANAGEMENT' },
    { id: 'unit-requests', label: 'Unit requests', icon: '📬', section: 'MANAGEMENT' },
    { id: 'move-out-requests', label: 'Move-out requests', icon: '🚚', section: 'MANAGEMENT' },
    { id: 'manage-rentals', label: 'Manage Rentals', icon: '🏠', section: 'MANAGEMENT' },
    {id: 'rent-status', label: 'Rent Status', icon: '💰', section: 'FINANCE'},
    { id: 'rent-evidence', label: 'Rent evidence', icon: '📎', section: 'FINANCE' },
    { id: 'water-usage', label: 'Water usage', icon: '💧', section: 'FINANCE' },
    { id: 'landlords', label: 'My Landlords', icon: '🤝', section: 'MANAGEMENT' },
    { id: 'maintenance', label: 'Maintenance', icon: '🔧', section: 'MANAGEMENT' },
    { id: 'tenant-score', label: 'Tenant Score', icon: '⭐', section: 'TRUST & SECURITY' },
    { id: 'analytics', label: 'Analytics', icon: '📊', section: 'TRUST & SECURITY' },
    { id: 'property-ratings', label: 'Property Ratings', icon: '⭐', section: 'TRUST & SECURITY' },
    { id: 'messages', label: 'Messages', icon: <IoChatbubbleEllipsesOutline />, section: 'COMMUNICATION' },
    { id: 'profile', label: 'My Profile', icon: '👤', section: 'SYSTEM' },
  ];

  const addNotification = (text) => {
    // Notification handler - can be wired to backend or notification system
    // In production, this would send to a notification service or display toast notifications
  };

  const handleBackToProperties = () => {
    setActiveSection('properties');
    localStorage.removeItem('agent_selected_property');
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'properties':
        return <AssignedProperties onNotify={addNotification} onNavigate={setActiveSection} />;
      case 'unit-requests':
        return <AgentUnitRequests navigationState={navigationState} onNavigate={handleNavigate} />;
      case 'manage-rentals':
        return <ManageRentals />;
      case 'move-out-requests':
        return <MoveOutRequests navigationState={navigationState} />;
      case 'rent-evidence':
        return <RentEvidenceView isAgentView />;
      case 'water-usage':
        return <WaterUsageView isAgentView />;
      case 'landlords':
        return <Landlords />;
      case 'rent-status':
        return <RentStatus isAgentView />;
      case 'manage-tenants':
        return <ManageTenants onNotify={addNotification} onBack={handleBackToProperties} />;
      case 'follow-payments': {
        // Get property ID from localStorage (set by AssignedProperties)
        const propertyData = localStorage.getItem('agent_selected_property');
        let propertyId = null;
        if (propertyData) {
          try {
            const property = JSON.parse(propertyData);
            propertyId = property.id;
          } catch (e) {
            console.error('Error parsing property data:', e);
          }
        }
        if (propertyId) {
          return <RentStatusDetails propertyId={propertyId} onBack={handleBackToProperties} />;
        }
        return <AssignedProperties onNotify={addNotification} onNavigate={setActiveSection} />;
      }
      case 'maintenance':
        return <PropertyMaintenanceView isAgentView />;
      case 'tenant-score':
        return <TenantScore variant="agent" navigationState={navigationState} />;
      case 'analytics':
        return <AgentAnalytics />;
      case 'property-ratings':
        return <PropertyRatings dashboardScope="AGENT" />;
      case 'profile':
        return <Profile />;
      case 'messages':
        return (
          <Messages
            userRole="Agent"
            threadId={navigationState?.threadId}
            threadData={navigationState?.threadData}
          />
        );
      case 'notifications':
        return <Notifications onNavigate={handleNavigate} userRole="Agent" />;
      default:
        return <AssignedProperties onNotify={addNotification} onNavigate={setActiveSection} />;
    }
  };

  return (
    <div className="dashboard">
      <Sidebar items={sidebarItems} activeItem={activeSection} onItemClick={handleNavigate} variant="landlord" />
      <div className="dashboard-content">
        <Header title="Agent Dashboard" userRole="Agent" activeSection={activeSection} onNavClick={handleNavigate} />
        <main className="dashboard-main">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default AgentDashboard;
